import React from 'react';
import { TemplateConfig } from '../types';
import './SlidePreview.css';

interface SlidePreviewProps {
  template: TemplateConfig;
  previewType: 'welcome' | 'product' | 'closing';
}

const SlidePreview: React.FC<SlidePreviewProps> = ({ template, previewType }) => {
  const sampleProduct = {
    name: 'Zaha Marsanne',
    brand: 'Telio',
    category: { name: 'białe' },
    subcategory: { name: 'wytrawne' },
    country: 'Argentyna',
    region: 'Dolina Uco, Tunuyán',
    grape_variety: ['Marsanne'],
    taste_profile: 'Kwasowy, z nutami jabłka i białych kwiatów',
    characteristics: 'Hołd winom doliny Rodanu oddany przez winiarzy - Alejandro Sejanovicha y Jeffa Mausbaca.',
    price_net: 138.00,
    image_url: '/media/sample_wine.png',
    product_type: { name: 'wine' },
    awards: ['96 punktów - Wine Enthusiast', '94 punkty - Robert Parker']
  };

  const renderWelcomeSlide = () => (
    <div className="preview-slide welcome-slide" 
         style={{
           backgroundColor: template.welcome?.backgroundColor || '#8B4513',
           backgroundImage: template.welcome?.backgroundImage ? `url(${template.welcome.backgroundImage})` : undefined,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat'
         }}>
      
      {template.welcome?.showLogo && (
        <div className="preview-logo">
          {template.welcome?.logoUrl ? 
            <img src={template.welcome.logoUrl} alt="Logo" /> : 
            <div className="logo-placeholder">Logo</div>
          }
        </div>
      )}
      
      
      <div className="preview-subtitle" style={{ 
        color: template.welcome?.subtitleColor || '#F3E7C4',
        fontFamily: template.welcome?.fontFamily || 'Book Antiqua',
        fontSize: `${(template.welcome?.fontSize || 16) * 0.8}px`
      }}>
        {template.welcome?.subtitle?.replace('{date}', new Date().toLocaleDateString('pl-PL')) || 'Przygotowana: ' + new Date().toLocaleDateString('pl-PL')}
      </div>
    </div>
  );

  const renderProductSlide = () => (
    <div className="preview-slide product-slide" 
         style={{
           backgroundColor: template.product?.backgroundColor || '#C8A982',
           backgroundImage: template.product?.backgroundUrl ? `url(${template.product.backgroundUrl})` : undefined,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat',
           fontFamily: template.product?.fontFamily || 'Book Antiqua',
           fontSize: `${template.product?.fontSize || 16}px`
         }}>
      <div className="preview-content">
        <div className="preview-image">
          {sampleProduct.image_url ? 
            <img src={sampleProduct.image_url} alt={sampleProduct.name} /> : 
            <div className="image-placeholder">Zdjęcie produktu</div>
          }
        </div>
        
        <div className="preview-details">
          <h2 className="preview-product-title" style={{ color: template.product?.titleColor || '#F3E7C4' }}>
            {sampleProduct.name}
          </h2>
          
          <div className="preview-product-info" style={{ color: template.product?.detailsColor || '#F3E7C4' }}>
            <p><strong>Producent:</strong><br/>{sampleProduct.brand}.</p>
            <p><strong>Produkt:</strong><br/>Wino {sampleProduct.category.name}, {sampleProduct.subcategory.name}.</p>
            <p><strong>Pochodzenie:</strong><br/>{sampleProduct.country}, {sampleProduct.region}</p>
            <p><strong>Szczep:</strong><br/>{sampleProduct.grape_variety.join(', ')}.</p>
            <p><strong>Smak:</strong><br/>{sampleProduct.taste_profile}</p>
            <p><strong>Cechy wyróżniające:</strong><br/>{sampleProduct.characteristics}</p>
          </div>
        </div>
        
        {template.product?.showAwards && (
          <div className="preview-awards">
            <h3 style={{ color: template.product?.titleColor || '#F3E7C4' }}>Nagrody i punktacje:</h3>
            <div className="awards-list" style={{ color: template.product?.detailsColor || '#F3E7C4' }}>
              {sampleProduct.awards.map((award, index) => (
                <p key={index}>{award}</p>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {template.product?.showPrice && (
        <div className="preview-price" style={{ color: template.product?.priceColor || '#F3E7C4' }}>
          Cena netto {sampleProduct.price_net.toFixed(2)} zł
        </div>
      )}
    </div>
  );

  const renderClosingSlide = () => (
    <div className="preview-slide closing-slide" 
         style={{
           backgroundColor: template.welcome?.backgroundColor || '#8B4513',
           backgroundImage: template.welcome?.backgroundImage ? `url(${template.welcome.backgroundImage})` : 'url(/media/background.jpg)',
           backgroundSize: '100% 100%',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat',
         }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
        padding: '40px 20px 20px 20px'
      }}>
        {/* Location 1 - Kazimierza */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginRight: '10px'
        }}>
          <img 
            src="/media/map1.png" 
            alt="Kazimierza" 
            style={{
              width: '100%',
              maxWidth: '300px',
              height: 'auto',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: '20px'
            }}
          />
          <div style={{
            color: '#F3E7C4',
            fontSize: `${template.product?.closingFontSize || 18}px`, // Use separate font size
            textAlign: 'center',
            lineHeight: '1.3',
            fontFamily: template.product?.fontFamily || 'Book Antiqua'
          }}>
            Ul. Jana Kazimierza 30 lok. 7<br/>
            01-248 Warszawa<br/>
            +48 22 487 89 58<br/>
            sklep@wawp.pl
          </div>
        </div>
        
        {/* Location 2 - Mińska */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          margin: '0 5px'
        }}>
          <img 
            src="/media/map2.png" 
            alt="Mińska" 
            style={{
              width: '100%',
              maxWidth: '300px',
              height: 'auto',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: '20px'
            }}
          />
          <div style={{
            color: '#F3E7C4',
            fontSize: `${template.product?.closingFontSize || 18}px`, // Use separate font size
            textAlign: 'center',
            lineHeight: '1.3',
            fontFamily: template.product?.fontFamily || 'Book Antiqua'
          }}>
            Ul. Mińska 23 lok. 4<br/>
            03-808 Warszawa<br/>
            +48 22 243 90 43<br/>
            minska@wawp.pl
          </div>
        </div>
        
        {/* Location 3 - Bergamotki */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginLeft: '10px'
        }}>
          <img 
            src="/media/map3.png" 
            alt="Bergamotki" 
            style={{
              width: '100%',
              maxWidth: '300px',
              height: 'auto',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginBottom: '20px'
            }}
          />
          <div style={{
            color: '#F3E7C4',
            fontSize: `${template.product?.closingFontSize || 18}px`, // Use separate font size
            textAlign: 'center',
            lineHeight: '1.3',
            fontFamily: template.product?.fontFamily || 'Book Antiqua'
          }}>
            Ul. Bergamotki 3 lok. 3<br/>
            02-765 Warszawa<br/>
            +48 22 245 29 25<br/>
            mokotow@wawp.pl
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="slide-preview-container">
      {previewType === 'welcome' && renderWelcomeSlide()}
      {previewType === 'product' && renderProductSlide()}
      {previewType === 'closing' && renderClosingSlide()}
    </div>
  );
};

export default SlidePreview;